import { z } from "zod";
// =============================================================================
// The product catalogue — request shapes and the limits that go with it.
//
// A catalogue entry is a real product with photographs of the real item. It
// exists so that when a post's picture shows a product, it shows THIS shop's
// product: the photographs go to the image model as references instead of the
// model inventing something plausible.
//
// Photographs arrive as multipart and are checked against media/imageTypes.ts;
// these schemas cover the fields travelling alongside them.
// =============================================================================
/**
 * How many photographs one product may hold.
 *
 * Six is about the number of genuinely different angles a small shop will take
 * of one item, and well under the image API's cap of sixteen references. The
 * binding constraint is not the API, it is that every reference is billed as
 * image tokens — see MAX_REFERENCE_IMAGES.
 */
export const MAX_PRODUCT_IMAGES = 6;
/**
 * How many of them are actually sent to the image model.
 *
 * Reference images bill as input image tokens, roughly in proportion to how
 * many there are, so this is a cost ceiling rather than a capability one. Two
 * or three views is where the fidelity gain flattens out; a fourth mostly buys
 * a bigger bill. The primary photograph is always among them.
 */
export const MAX_REFERENCE_IMAGES = 3;
/**
 * Multipart turns every field into a string, so a checkbox arrives as "true".
 * Same shape as uploadPhotoSchema's `enhance` for the same reason.
 */
const formBoolean = (fallback) => z
    .union([z.boolean(), z.literal("true"), z.literal("false")])
    .default(fallback)
    .transform((v) => v === true || v === "true");
export const productNameSchema = z
    .string()
    .trim()
    .min(1, "Give the product a name")
    // Short on purpose: this is the string the caption model returns to name its
    // choice, and it is matched back against the catalogue. A paragraph would
    // match nothing.
    .max(120);
export const brandProductFieldsSchema = z.object({
    name: productNameSchema,
    /** Size, variety, what it comes in — anything that makes a picture truer. */
    description: z.string().trim().max(600).optional().nullable(),
    /**
     * Free text, caption-only.
     *
     * Text rather than a number and a currency because what an owner wants to
     * say is "GHS 45 a kilo" or "from GHS 200", and neither is a decimal. It
     * never reaches an image prompt: generated images carry no text at all.
     */
    price: z.string().trim().max(80).optional().nullable(),
    /** False while it is out of stock or out of season. */
    isActive: formBoolean(true),
    /**
     * Tidy the background on the way in.
     *
     * Default ON, because a blank background is what makes a catalogue
     * photograph usable: the image model lifts the product out of it to build a
     * scene, and a shop counter behind it gets lifted out with it. Most owners
     * are photographing on whatever surface is to hand.
     *
     * Off for a photograph that is already a clean studio shot, and ignored
     * entirely for a brand whose aiImagePolicy is `off` — the original is always
     * kept either way.
     */
    tidy: formBoolean(true),
});
/** Creating a product. The first photograph arrives with it, as multipart. */
export const createProductSchema = brandProductFieldsSchema.extend({
    brandId: z.string().uuid(),
});
/**
 * Editing one. Every field optional — `isActive` included, which is why this
 * cannot simply be `.partial()` on the schema above: the form-boolean has a
 * default, and `.partial()` would keep applying it and switch a product back on
 * every time its name was corrected.
 */
export const updateProductSchema = z.object({
    name: productNameSchema.optional(),
    description: z.string().trim().max(600).optional().nullable(),
    price: z.string().trim().max(80).optional().nullable(),
    isActive: z.union([z.boolean(), z.literal("true"), z.literal("false")]).optional(),
});
export const listProductsSchema = z.object({
    brandId: z.string().uuid(),
    /** Out-of-stock products are included by default; the dashboard shows them. */
    activeOnly: z
        .union([z.boolean(), z.literal("true"), z.literal("false")])
        .optional()
        .transform((v) => v === true || v === "true"),
});
/** Adding another photograph to a product that already exists. */
export const addProductImageSchema = z.object({
    /** Make this one the primary straight away. */
    primary: formBoolean(false),
    /** See brandProductFieldsSchema.tidy. */
    tidy: formBoolean(true),
});
/** Reordering, so the owner's own order drives the dashboard and the rotation. */
export const reorderProductsSchema = z.object({
    productIds: z.array(z.string().uuid()).min(1).max(500),
});
//# sourceMappingURL=catalogue.js.map